import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const chapters = [
  { title: "O ponto onde tudo começa", text: "Reconhecer o momento em que a vida já não cabe. A mudança não vem da coragem — vem do cansaço.", exercise: "O que te trouxe a esta página." },
  { title: "O que te trouxe até aqui", text: "Olhar para o passado como preparação, não como erro. Cada decisão foi um degrau.", exercise: "A cronologia da tua inquietação." },
  { title: "O que ainda carregas", text: "Distinguires o que é teu do que te foi imposto — expectativas, papéis, versões de ti que outros desenharam.", exercise: "O inventário do que carregas." },
  { title: "Onde te perdes", text: "Os silêncios. As versões aceitáveis. Os sítios onde, sem dares por isso, deixas de aparecer.", exercise: "O mapa dos teus desaparecimentos." },
  { title: "O que já sabes", text: "Não te falta informação — falta-te a permissão para agires sobre o que já sabes.", exercise: "Permissão escrita." },
  { title: "O medo por trás da decisão", text: "Coragem é decidires que aquilo que queres é maior do que aquilo que temes.", exercise: "Dar nome ao medo." },
  { title: "Clareza não é certeza", text: "A clareza não chega antes do movimento. Chega através dele.", exercise: "O que já não quero." },
  { title: "Dois tipos de decisão", text: "As decisões visíveis — e as decisões interiores que mudam tudo, sem que ninguém à tua volta dê por isso.", exercise: "Que decisão é esta, afinal?" },
  { title: "O preço de continuares igual", text: "Há um custo silencioso em ficares onde estás. Um preço que pagas todos os dias.", exercise: "O preço, em palavras tuas." },
  { title: "Um passo, só um", text: "Não precisas de um plano grandioso. Precisas de um passo concreto, real, hoje.", exercise: "O primeiro passo possível." },
];

export default function Chapters() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="capitulos"
      data-testid="chapters-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-cream"
    >
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-14"
        >
          <div className="md:col-span-3">
            <span className="section-num">03 · Índice</span>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.02] mb-6">
              Dez capítulos.
              <br />
              Uma viagem <span className="font-serif-display text-orange normal-case lowercase">para dentro.</span>
            </h2>
            <p className="text-lg text-ink-soft max-w-2xl leading-relaxed">
              Cada capítulo é um ensaio breve seguido de um exercício. Não para ler — para fazer.
              Lentamente, com caneta na mão.
            </p>
          </div>
        </motion.div>

        <div className="border-t border-line">
          {chapters.map((c, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.25, delay: i * 0.015 }}
                className="border-b border-line"
                data-testid={`chapter-item-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`chapter-toggle-${i}`}
                  className="w-full flex items-baseline gap-6 md:gap-10 py-7 text-left group"
                >
                  <span
                    className={`font-display text-xl md:text-2xl flex-shrink-0 transition-colors duration-300 ${
                      isOpen ? "text-orange" : "text-ink-soft group-hover:text-orange"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`flex-1 font-display text-lg md:text-2xl transition-colors duration-300 leading-tight ${
                      isOpen ? "text-ink" : "text-ink group-hover:text-orange"
                    }`}
                  >
                    {c.title}
                  </span>
                  <span
                    className={`text-2xl text-ink-soft transition-transform duration-300 flex-shrink-0 ${
                      isOpen ? "rotate-45 text-orange" : ""
                    }`}
                  >
                    +
                  </span>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pb-8 md:pl-16 max-w-3xl">
                        <p className="text-base md:text-lg text-ink-soft leading-relaxed mb-5">
                          {c.text}
                        </p>
                        <div className="flex items-baseline gap-3 pt-4 border-t border-line">
                          <span className="smallcaps text-orange">Exercício</span>
                          <span className="font-serif-display text-lg text-ink">{c.exercise}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
